import React, { useMemo } from 'react';
import { Image, StyleSheet, Text, View } from 'react-native';
import Svg, { Defs, LinearGradient, Rect, Stop } from 'react-native-svg';

import {
	MainUserIcon,
	TournamentCardPlateBronze,
	TournamentCardPlateGold,
	TournamentCardPlateSilver,
	TournamentsTrofeeBronze,
	TournamentsTrofeeGold,
	TournamentsTrofeeSilver,
	TournamentWinCoins,
} from '../../../assets';
import ChipPng from '../../../assets/chip.png';
import { useSvgId } from '../../../utils/hooks/useSvgId';
import { Pressable } from '../common/Touchable';

// Geometry from the Figma card "Table Card 3" (2747:17088) on a 226x416 plate; every offset is
// scaled from these so the card keeps its proportions at whatever width the carousel hands it.
export const TOURNAMENT_CARD_BASE_WIDTH = 226;
export const TOURNAMENT_CARD_BASE_HEIGHT = 416;

// Where each block stands on the card. 'stage' is the mobile carousel's 256x376 card ("Table
// Card 3", 8526:39976): a smaller emblem, everything under it moved up, the buttons centred.
const LAYOUTS = {
	tall: {
		width: TOURNAMENT_CARD_BASE_WIDTH,
		height: TOURNAMENT_CARD_BASE_HEIGHT,
		badgeLeft: 120,
		emblem: { top: 29, size: 140 },
		winTop: 160,
		chipsTop: 194,
		prizeTop: 218,
		divider: { top: 263, left: 39 },
		entryLabelTop: 265,
		entryRowTop: 281,
		join: { top: 320, left: 26 },
		activeTables: { top: 366, left: 25 },
	},
	stage: {
		width: 256,
		height: 376,
		badgeLeft: 150,
		emblem: { top: 22, size: 109 },
		winTop: 122,
		chipsTop: 156,
		prizeTop: 180,
		divider: { top: 225, left: 53 },
		entryLabelTop: 227,
		entryRowTop: 243,
		join: { top: 282, left: 38 },
		activeTables: { top: 328, left: 38 },
	},
} as const;

// The design sets WIN / prize / entry in "Sofia Sans Condensed", which this app does not bundle —
// only "Sofia Sans" and "Sofia Sans Semi Condensed". Semi Condensed is the closest available.
const FONT = 'Sofia Sans';
const FONT_CONDENSED = 'Sofia Sans Semi Condensed';

const EMBLEMS: Record<string, any> = {
	bronze: TournamentsTrofeeBronze,
	silver: TournamentsTrofeeSilver,
	gold: TournamentsTrofeeGold,
};

// Each tier gets its own plate (Figma "Tournaments Elements": Table Card 7 / 9 / 8), baked from
// the design's layer stack because the wheel squashes side cards too far for a 1px CSS border.
const PLATES: Record<string, any> = {
	bronze: TournamentCardPlateBronze,
	silver: TournamentCardPlateSilver,
	gold: TournamentCardPlateGold,
};

// Matches each plate's darkest edge, so the iOS shadow layer under it is invisible.
const PLATE_BASE: Record<string, string> = {
	bronze: '#1B0020',
	silver: '#04101F',
	gold: '#150E00',
};

const colors = {
	win: '#FFC40A',
	prize: '#FFB808',
	divider: '#FFAD05',
	entryLabel: '#FFD10F',
	entryValue: '#FFB005',
	badgeBorder: 'rgba(255,209,77,0.55)',
	badgeTop: '#21170F',
	joinBorder: '#FFF908',
	joinLabel: '#330E07',
	activeTablesPlate: '#000000',
	activeTablesBorder: '#DA600E',
	activeTablesLabel: '#FFFF9E',
	white: '#FFFFFF',
} as const;

// The gold "Join table" fill: the design's 0deg gradient, restated top-down.
const JOIN_STOPS: Array<[string, string]> = [
	['0', '#FFFF9E'],
	['0.3962', '#FFF908'],
	['0.9197', '#F58F00'],
	['1', '#B25200'],
];

const glow = {
	shadowColor: '#FF7200',
	shadowOffset: { width: 0, height: 0 },
	shadowOpacity: 0.7,
	shadowRadius: 15,
	elevation: 12,
} as const;

const buttonGlow = {
	shadowColor: '#FF7200',
	shadowOffset: { width: 0, height: 0 },
	shadowOpacity: 0.7,
	shadowRadius: 5.5,
	elevation: 5,
} as const;

export type TournamentCarouselCardProps = {
	tier: 'bronze' | 'silver' | 'gold';
	prizeLabel: string;
	prizeValue: string;
	entryLabel: string;
	entryValue: string;
	/** "Players" - the badge's first line. */
	playersLabel: string;
	/** "1 of 8" - the badge's second line. */
	playersValue: string;
	registerLabel: string;
	activeTablesLabel: string;
	width?: number;
	/** 'stage': the mobile carousel's 256x376 card; 'tall' (default): the 226x416 one. */
	shape?: keyof typeof LAYOUTS;
	onRegister?: () => void;
	onActiveTables?: () => void;
	/** Tapping the card anywhere but its two buttons - opens the tournament's info modal. */
	onPress?: () => void;
};

const TournamentCarouselCard = ({
	tier,
	prizeLabel,
	prizeValue,
	entryLabel,
	entryValue,
	playersLabel,
	playersValue,
	registerLabel,
	activeTablesLabel,
	width = TOURNAMENT_CARD_BASE_WIDTH,
	shape = 'tall',
	onRegister,
	onActiveTables,
	onPress,
}: TournamentCarouselCardProps) => {
	const layout = LAYOUTS[shape];
	const scale = width / layout.width;
	const joinFillId = useSvgId('tournamentJoinFill');

	const g = useMemo(() => {
		const s = (value: number) => value * scale;
		const height = s(layout.height);
		const joinBorder = Math.max(0.5, s(1));

		return {
			height,
			radius: s(15),
			card: { width, height },
			badge: {
				top: s(7),
				left: s(layout.badgeLeft),
				width: s(98),
				borderRadius: s(21),
				paddingHorizontal: s(13),
				paddingVertical: s(7),
			},
			badgeIcon: { width: s(16), height: s(18) },
			badgeLabel: { fontSize: s(11), lineHeight: s(13.2) },
			badgeValue: { fontSize: s(10), lineHeight: s(12) },
			emblem: { top: s(layout.emblem.top), size: s(layout.emblem.size) },
			win: { top: s(layout.winTop), fontSize: s(24), lineHeight: s(34) },
			chips: { top: s(layout.chipsTop), width: s(50), height: s(28) },
			prize: { top: s(layout.prizeTop), fontSize: s(32), lineHeight: s(47) },
			divider: {
				top: s(layout.divider.top),
				left: s(layout.divider.left),
				width: s(150),
				height: Math.max(1, s(1.5)),
			},
			entryLabel: { top: s(layout.entryLabelTop), fontSize: s(14), lineHeight: s(22) },
			entryRow: { top: s(layout.entryRowTop), height: s(32), gap: s(6) },
			entryIcon: { width: s(21), height: s(22) },
			entryValue: { fontSize: s(20), lineHeight: s(32) },
			join: {
				top: s(layout.join.top),
				left: s(layout.join.left),
				width: s(180),
				height: s(34),
				borderRadius: s(12),
				borderWidth: joinBorder,
			},
			// The gradient fill sits inside the button's border, same reason as the card plate.
			joinFill: {
				width: s(180) - joinBorder * 2,
				height: s(34) - joinBorder * 2,
				rx: s(12) - joinBorder,
			},
			joinLabel: { fontSize: s(14), letterSpacing: s(0.055) },
			activeTables: {
				top: s(layout.activeTables.top),
				left: s(layout.activeTables.left),
				width: s(180),
				height: s(34),
				borderRadius: s(10),
				borderWidth: Math.max(0.5, s(0.2)),
			},
			activeTablesLabel: { fontSize: s(14), letterSpacing: s(0.055) },
		};
	}, [layout, scale, width]);

	return (
		<Pressable
			onPress={onPress}
			disabled={!onPress}
			style={[
				styles.card,
				g.card,
				{ borderRadius: g.radius, backgroundColor: PLATE_BASE[tier] ?? PLATE_BASE.bronze },
			]}
		>
			{/* Android sizes an absolutely-filled Image from the source's intrinsic pixels (452x832
			    here) instead of the inset box, so the plate spilled far past the card and swallowed
			    the whole carousel. The card's own size, restated, pins it. */}
			<Image
				source={PLATES[tier] ?? PLATES.bronze}
				style={[styles.plate, { width, height: g.height, borderRadius: g.radius }]}
				// The plates are baked for the tall card; the stage's shorter one keeps their rim.
				resizeMode={shape === 'stage' ? 'stretch' : 'cover'}
			/>

			<View style={[styles.badge, g.badge]}>
				<View style={styles.badgeRow}>
					<Image source={MainUserIcon} style={g.badgeIcon} resizeMode='contain' />
					<View style={styles.badgeTextWrap}>
						<Text numberOfLines={1} style={[styles.badgeLabel, g.badgeLabel]}>
							{playersLabel}
						</Text>
						<Text numberOfLines={1} style={[styles.badgeValue, g.badgeValue]}>
							{playersValue}
						</Text>
					</View>
				</View>
			</View>

			<Image
				source={EMBLEMS[tier] ?? EMBLEMS.bronze}
				style={[styles.emblem, { top: g.emblem.top, width: g.emblem.size, height: g.emblem.size }]}
				resizeMode='contain'
			/>

			<Text numberOfLines={1} style={[styles.win, g.win, { top: g.win.top }]}>
				{prizeLabel}
			</Text>

			<Image
				source={TournamentWinCoins}
				style={[styles.chips, { top: g.chips.top, width: g.chips.width, height: g.chips.height }]}
				resizeMode='contain'
			/>

			<Text numberOfLines={1} style={[styles.prize, g.prize, { top: g.prize.top }]}>
				{prizeValue}
			</Text>

			<View style={[styles.divider, g.divider]} />

			<Text numberOfLines={1} style={[styles.entryLabel, g.entryLabel, { top: g.entryLabel.top }]}>
				{entryLabel}
			</Text>

			<View
				style={[
					styles.entryRow,
					{ top: g.entryRow.top, height: g.entryRow.height, gap: g.entryRow.gap },
				]}
			>
				<Image source={ChipPng} style={g.entryIcon} resizeMode='contain' />
				<Text numberOfLines={1} style={[styles.entryValue, g.entryValue]}>
					{entryValue}
				</Text>
			</View>

			<Pressable onPress={onRegister} style={[styles.joinButton, g.join]}>
				<Svg width={g.joinFill.width} height={g.joinFill.height} style={styles.plate}>
					<Defs>
						<LinearGradient id={joinFillId} x1='0' y1='0' x2='0' y2='1'>
							{JOIN_STOPS.map(([offset, color]) => (
								<Stop key={offset} offset={offset} stopColor={color} />
							))}
						</LinearGradient>
					</Defs>
					<Rect
						x={0}
						y={0}
						width={g.joinFill.width}
						height={g.joinFill.height}
						rx={g.joinFill.rx}
						fill={`url(#${joinFillId})`}
					/>
				</Svg>
				<Text numberOfLines={1} style={[styles.joinLabel, g.joinLabel]}>
					{registerLabel}
				</Text>
			</Pressable>

			<Pressable onPress={onActiveTables} style={[styles.activeTablesButton, g.activeTables]}>
				<Text numberOfLines={1} style={[styles.activeTablesLabel, g.activeTablesLabel]}>
					{activeTablesLabel}
				</Text>
			</Pressable>
		</Pressable>
	);
};

const styles = StyleSheet.create({
	// The per-tier backgroundColor gives the outer glow an opaque layer to hang off on iOS; the
	// plate paints over it.
	card: {
		...glow,
	},
	plate: {
		...StyleSheet.absoluteFillObject,
	},
	badge: {
		position: 'absolute',
		borderWidth: 1,
		borderColor: colors.badgeBorder,
		backgroundColor: colors.badgeTop,
		zIndex: 3,
	},
	badgeRow: {
		flexDirection: 'row',
		alignItems: 'center',
		justifyContent: 'space-between',
	},
	badgeTextWrap: {
		flex: 1,
		alignItems: 'center',
	},
	badgeLabel: {
		color: colors.white,
		fontFamily: FONT,
		fontWeight: '500',
		textAlign: 'center',
	},
	badgeValue: {
		color: colors.white,
		fontFamily: FONT,
		fontWeight: '600',
		textAlign: 'center',
	},
	emblem: {
		position: 'absolute',
		alignSelf: 'center',
		zIndex: 2,
	},
	win: {
		position: 'absolute',
		left: 0,
		right: 0,
		textAlign: 'center',
		color: colors.win,
		fontFamily: FONT_CONDENSED,
		fontWeight: '900',
		zIndex: 2,
	},
	chips: {
		position: 'absolute',
		alignSelf: 'center',
		zIndex: 3,
	},
	prize: {
		position: 'absolute',
		left: 0,
		right: 0,
		textAlign: 'center',
		color: colors.prize,
		fontFamily: FONT_CONDENSED,
		fontWeight: '900',
		zIndex: 2,
	},
	divider: {
		position: 'absolute',
		backgroundColor: colors.divider,
		zIndex: 2,
	},
	entryLabel: {
		position: 'absolute',
		left: 0,
		right: 0,
		textAlign: 'center',
		color: colors.entryLabel,
		fontFamily: FONT,
		fontWeight: '700',
		zIndex: 2,
	},
	entryRow: {
		position: 'absolute',
		left: 0,
		right: 0,
		flexDirection: 'row',
		alignItems: 'center',
		justifyContent: 'center',
		zIndex: 2,
	},
	entryValue: {
		color: colors.entryValue,
		fontFamily: FONT_CONDENSED,
		fontWeight: '900',
	},
	joinButton: {
		position: 'absolute',
		alignItems: 'center',
		justifyContent: 'center',
		borderColor: colors.joinBorder,
		backgroundColor: colors.joinBorder,
		// No overflow clip: on iOS it hides the view's own glow. The fill is a rounded rect sized
		// inside the border (joinFill), so there is nothing to clip.
		zIndex: 3,
		...buttonGlow,
	},
	joinLabel: {
		color: colors.joinLabel,
		fontFamily: FONT,
		fontWeight: '900',
		textAlign: 'center',
	},
	activeTablesButton: {
		position: 'absolute',
		alignItems: 'center',
		justifyContent: 'center',
		borderColor: colors.activeTablesBorder,
		backgroundColor: colors.activeTablesPlate,
		zIndex: 3,
		...buttonGlow,
	},
	activeTablesLabel: {
		color: colors.activeTablesLabel,
		fontFamily: FONT,
		fontWeight: '900',
		textAlign: 'center',
	},
});

export default TournamentCarouselCard;
